import { compact } from '@leland-dev/leland-ui-library';
import { unionBy } from 'lodash-es';
import React, { useCallback, useMemo, useState } from 'react';

import type { Scalars } from '../../__generated-gql-types__/globalTypes';
import { useDebounce } from '../../utils/hooks/useDebounce';
import { OrganizationImg, OrganizationType } from '../common/OrganizationImg';
import {
  MultiTypeahead,
  type MultiTypeaheadProps,
  SingleTypeahead,
  type SingleTypeaheadProps,
} from '../inputs/Typeahead';
import { type SelectOptionType } from '../inputs/selects';

import {
  type CompanyTypeaheadCompanyFragment,
  useCompanyTypeaheadCreateCompanyMutation,
  useCompanyTypeaheadQuery,
} from './__generated-gql-types__/CompanyTypeahead.generated';

export type CompanyTypeaheadCompany = CompanyTypeaheadCompanyFragment;

const isCompanyTypeaheadCompanyEqual = (
  c1: CompanyTypeaheadCompany,
  c2: CompanyTypeaheadCompany,
) => c1.id === c2.id;

interface CommonCompanyTypeaheadProps {
  categoryUrn?: Scalars['CategoryUrn']['output'];
  onlyUseDB?: boolean;
}

type SingleCompanyTypeaheadProps = Omit<
  SingleTypeaheadProps<CompanyTypeaheadCompany>,
  'options' | 'onInputChange'
> &
  CommonCompanyTypeaheadProps;

export const SingleCompanyTypeahead: React.FC<SingleCompanyTypeaheadProps> = ({
  value,
  placeholder = 'Search companies...',
  categoryUrn,
  onChange: onChangeProp,
  onlyUseDB,
  ...props
}) => {
  const queryProps = useCompanyTypeaheadQueryData(
    { value },
    categoryUrn,
    onlyUseDB,
  );

  const [createCompany] = useCompanyTypeaheadCreateCompanyMutation();

  // handle creation of any companies that do not yet exist in our DB
  const onChange = useCallback(
    async (company: Nullable<CompanyTypeaheadCompanyFragment>) => {
      let validatedCompany = company;
      if (company && !company.id) {
        const { data } = await createCompany({
          variables: {
            company: {
              name: company.name,
              domain: company.domain,
            },
          },
        });
        validatedCompany = data?.createCompany ?? null;
      }
      return onChangeProp(validatedCompany);
    },
    [createCompany, onChangeProp],
  );

  return (
    <SingleTypeahead
      {...props}
      onChange={onChange}
      value={value}
      isValueEqual={isCompanyTypeaheadCompanyEqual}
      placeholder={placeholder}
      renderOption={renderCompanyOption}
      {...queryProps}
    />
  );
};

type MultiCompanyTypeaheadProps = Omit<
  MultiTypeaheadProps<CompanyTypeaheadCompany>,
  'options' | 'onInputChange'
> &
  CommonCompanyTypeaheadProps;

export const MultiCompanyTypeahead: React.FC<MultiCompanyTypeaheadProps> = ({
  values,
  placeholder = 'Search companies...',
  categoryUrn,
  onChange: onChangeProp,
  ...props
}) => {
  const queryProps = useCompanyTypeaheadQueryData({ values }, categoryUrn);

  const [createCompany] = useCompanyTypeaheadCreateCompanyMutation();

  // handle creation of any companies that do not yet exist in our DB
  const onChange = useCallback(
    async (companies: CompanyTypeaheadCompanyFragment[]) => {
      const validatedCompanies = (
        await Promise.all(
          companies.map(async (company) => {
            if (company.id) {
              return company;
            }
            const { data } = await createCompany({
              variables: {
                company: { name: company.name, domain: company.domain },
              },
            });
            return data?.createCompany ?? null;
          }),
        )
      ).filter(compact);

      return onChangeProp(validatedCompanies);
    },
    [createCompany, onChangeProp],
  );

  return (
    <MultiTypeahead
      {...props}
      onChange={onChange}
      values={values}
      isValueEqual={isCompanyTypeaheadCompanyEqual}
      placeholder={placeholder}
      renderOption={renderCompanyOption}
      {...queryProps}
    />
  );
};

const companyToSelectOptionType = (
  company: CompanyTypeaheadCompany,
): SelectOptionType<CompanyTypeaheadCompany> => ({
  value: company,
  label: company.name,
});

const useCompanyTypeaheadQueryData = (
  {
    value,
    values,
  }: {
    value?: Nullable<CompanyTypeaheadCompany>;
    values?: CompanyTypeaheadCompany[];
  },
  categoryUrn?: Scalars['CategoryUrn']['output'],
  onlyUseDB?: boolean,
) => {
  const [rawQuery, setRawQuery] = useState<string>('');
  const query = useDebounce(rawQuery, 200);
  const onInputChange = useCallback((input: string) => setRawQuery(input), []);

  const { data, loading } = useCompanyTypeaheadQuery({
    variables: { query, categoryUrn, onlyUseDB: onlyUseDB ?? false },
  });

  const options: Array<SelectOptionType<CompanyTypeaheadCompany>> =
    useMemo(() => {
      const companies = unionBy(
        data?.companyTypeahead ?? [],
        value ? [value] : values,
        (company) => `${company.name}::${company.domain}`,
      );
      return companies.map(companyToSelectOptionType);
    }, [data?.companyTypeahead, value, values]);

  return { onInputChange, options, loading };
};

const renderCompanyOption = (company: CompanyTypeaheadCompany) => (
  <div className="flex items-center">
    <OrganizationImg
      nonSizeClassName="mr-2 rounded"
      pictureLink={company.logoLink}
      name={company.name}
      type={OrganizationType.COMPANY}
    />
    {company.name}
  </div>
);
